import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode, BisacSearchHit } from "reedy-data/api";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/table";
import { Text } from "components/text";
import { ThemaCodesTableSkeletonRows } from "components/ThemaCodes/ThemaCodesTableSkeletonRows";
import { groupBisacSubjects } from "./bisacSubjects";
import { BisacCodesTableCodeRow } from "./BisacCodesTableCodeRow";
import { BisacCodesTableSubjectRow } from "./BisacCodesTableSubjectRow";

const NOTATION_COLUMN_WIDTH = 160;
const COLUMN_COUNT = 3;

interface BisacCodesTableProps {
  codes: BisacSearchHit[] | undefined;
  searchQuery: string;
  openSubjectPrefix: string | null;
  onOpenSubjectPrefixChange: (prefix: string | null) => void;
  isLoading: boolean;
  isError: boolean;
  renderRowAction?: (code: BisacCode) => ReactNode;
}

export const BisacCodesTable = ({
  codes = [],
  searchQuery,
  openSubjectPrefix,
  onOpenSubjectPrefixChange,
  isLoading,
  isError,
  renderRowAction,
}: BisacCodesTableProps) => {
  const { t } = useTranslation("components");
  const isSearch = Boolean(searchQuery);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [openSubjectPrefix, searchQuery]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <ThemaCodesTableSkeletonRows
          level={0}
          notationColumnWidth={NOTATION_COLUMN_WIDTH}
        />
      );
    }

    if (isError || codes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} className="h-32 text-center">
            {isError ? (
              <Text variant="error">
                {isSearch
                  ? t("components:bisacCodesTable.errorSearch")
                  : t("components:bisacCodesTable.errorCatalogue")}
              </Text>
            ) : (
              <Text variant="muted">
                {t("components:bisacCodesTable.noResults")}
              </Text>
            )}
          </TableCell>
        </TableRow>
      );
    }

    if (isSearch) {
      return codes.map((code) => (
        <BisacCodesTableCodeRow
          key={code.code}
          code={code}
          highlightPrefix={searchQuery}
          renderAction={renderRowAction}
        />
      ));
    }

    const subjects = groupBisacSubjects(codes);
    const openSubject = subjects.find(
      ({ prefix }) => prefix === openSubjectPrefix,
    );

    // One keyed list for both states, so the toggled row keeps keyboard focus.
    return (openSubject ? [openSubject] : subjects).flatMap((subject) => [
      <BisacCodesTableSubjectRow
        key={subject.prefix}
        subject={subject}
        isOpen={subject === openSubject}
        onToggle={() =>
          onOpenSubjectPrefixChange(
            subject === openSubject ? null : subject.prefix,
          )
        }
      />,
      ...(subject === openSubject
        ? subject.codes.map((code) => (
            <BisacCodesTableCodeRow
              key={code.code}
              code={code}
              indented
              renderAction={renderRowAction}
            />
          ))
        : []),
    ]);
  };

  return (
    <div className="flex-1 flex flex-col border border-secondary-200 rounded-md overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <table className="w-full caption-bottom text-sm table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-secondary-50">
            <TableRow>
              <TableHead
                className="pl-4 text-sm font-medium text-secondary-700"
                style={{ width: NOTATION_COLUMN_WIDTH }}
              >
                {t("components:bisacCodesTable.notation")}
              </TableHead>
              <TableHead className="text-sm font-medium text-secondary-700">
                {t("components:bisacCodesTable.subjectHeading")}
              </TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>{renderBody()}</TableBody>
        </table>
      </div>
    </div>
  );
};
